import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Card, Table, Button, Space, Modal, Input, message, 
  Checkbox, Row, Col, Tooltip, Typography, Spin, Upload, Select,
  Tag
} from 'antd';
import { 
  PlusOutlined, DeleteOutlined, ReloadOutlined, CopyOutlined,
  DownloadOutlined, UploadOutlined, EyeOutlined, EyeInvisibleOutlined,
  FileExcelOutlined, ImportOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// BTC 地址验证和工具函数
const validateBtcAddress = (address: string): { isValid: boolean; type?: string } => {
  // P2PKH (Legacy) - 以1开头
  if (/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address)) {
    return { isValid: true, type: 'P2PKH (Legacy)' };
  }
  
  // P2SH (Script Hash) - 以3开头
  if (/^3[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address)) {
    return { isValid: true, type: 'P2SH (Script Hash)' };
  }
  
  // Bech32 (SegWit v0) - 以bc1q开头
  if (/^bc1q[a-z0-9]{38,58}$/.test(address)) {
    return { isValid: true, type: 'Bech32 (SegWit v0)' };
  }
  
  // Bech32m (SegWit v1, Taproot) - 以bc1p开头
  if (/^bc1p[a-z0-9]{58}$/.test(address)) {
    return { isValid: true, type: 'Bech32m (Taproot)' };
  }
  
  return { isValid: false };
};

// 接口定义
interface BtcWallet {
  id: number | string; // 支持 number 和 string 类型，用于合计行
  address: string;
  addressType: string;
  balance: string; // BTC 余额
  utxoCount: number; // UTXO 数量
  largeUtxoCount: number; // 大于 0.00001 BTC 的 UTXO 数量
  txCount: number; // 交易数量
  isLoading?: boolean;
}

interface UtxoInfo {
  txid: string;
  vout: number;
  value: number; // satoshis
  confirmations: number;
}

const WalletBtc: React.FC = () => {
  // 状态管理
  const [wallets, setWallets] = useState<BtcWallet[]>([]);
  const [selectedWalletIds, setSelectedWalletIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState<Record<number, boolean>>({});
  
  // Modal 状态
  const [isAddWalletVisible, setIsAddWalletVisible] = useState(false);
  const [isBatchImportVisible, setIsBatchImportVisible] = useState(false);
  const [isJsonImportVisible, setIsJsonImportVisible] = useState(false);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [newWalletAddress, setNewWalletAddress] = useState('');
  const [batchAddresses, setBatchAddresses] = useState('');
  const [exportFormat, setExportFormat] = useState<'json' | 'excel'>('json');
  const [importFileList, setImportFileList] = useState<any[]>([]);

  // UI 控制状态
  const [showRowTotal, setShowRowTotal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  // 样式定义 - 与 EVM 页面保持一致
  const buttonStyle = {
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '14px',
    height: '36px',
    minWidth: '80px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  };

  const componentStyle = {
    borderRadius: '6px',
    fontSize: '14px'
  };

  // 安全的JSON序列化函数
  const safeStringify = useCallback((obj: any): string => {
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'bigint') {
        return value.toString();
      }
      return value;
    });
  }, []);

  // 从localStorage加载数据
  const loadDataFromStorage = useCallback(() => {
    try {
      const storedWallets = localStorage.getItem('btc_wallet_manager_wallets');
      if (storedWallets) {
        setWallets(JSON.parse(storedWallets));
      }
    } catch (error) {
      console.error('加载BTC钱包数据失败:', error);
    }
  }, []);

  // 保存数据到localStorage
  const saveDataToStorage = useCallback(() => {
    try {
      localStorage.setItem('btc_wallet_manager_wallets', safeStringify(wallets));
      console.log('BTC钱包数据已保存到localStorage');
    } catch (error) {
      console.error('保存BTC钱包数据失败:', error);
    }
  }, [wallets, safeStringify]);

  // 添加钱包
  const handleAddWallet = () => {
    const address = newWalletAddress.trim();
    if (!address) {
      message.error('请输入BTC地址');
      return;
    }

    const validation = validateBtcAddress(address);
    if (!validation.isValid) {
      message.error('无效的BTC地址格式');
      return;
    }

    // 检查是否已存在
    const existingWallet = wallets.find(w => w.address === address);
    if (existingWallet) {
      message.warning('该BTC地址已存在');
      return;
    }

    const newWallet: BtcWallet = {
      id: Date.now(),
      address: address,
      addressType: validation.type!,
      balance: '0',
      utxoCount: 0,
      largeUtxoCount: 0,
      txCount: 0
    };

    setWallets(prev => [...prev, newWallet]);
    setNewWalletAddress('');
    setIsAddWalletVisible(false);
    message.success('BTC地址添加成功');
  };

  // 删除钱包
  const handleDeleteWallet = (walletId: number) => {
    setWallets(prev => prev.filter(w => w.id !== walletId));
    setSelectedWalletIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(walletId);
      return newSet;
    });
    message.success('BTC钱包已删除');
  };

  // 批量导入钱包地址
  const handleBatchImport = () => {
    const addresses = batchAddresses
      .split('\n')
      .map(addr => addr.trim())
      .filter(addr => addr.length > 0);

    if (addresses.length === 0) {
      message.error('请输入至少一个BTC地址');
      return;
    }

    let successCount = 0;
    let duplicateCount = 0;
    let invalidCount = 0;

    addresses.forEach(address => {
      const validation = validateBtcAddress(address);
      if (!validation.isValid) {
        invalidCount++;
        return;
      }

      const existingWallet = wallets.find(w => w.address === address);
      if (existingWallet) {
        duplicateCount++;
        return;
      }

      // 直接添加钱包，不调用handleAddWallet函数
      const newWallet: BtcWallet = {
        id: Date.now() + successCount, // 确保ID唯一
        address: address,
        addressType: validation.type!,
        balance: '0',
        utxoCount: 0,
        largeUtxoCount: 0,
        txCount: 0
      };

      setWallets(prev => [...prev, newWallet]);
      successCount++;
    });

    // 显示导入结果
    const resultMessages = [];
    if (successCount > 0) resultMessages.push(`成功导入 ${successCount} 个地址`);
    if (duplicateCount > 0) resultMessages.push(`跳过 ${duplicateCount} 个重复地址`);
    if (invalidCount > 0) resultMessages.push(`忽略 ${invalidCount} 个无效地址`);

    message.success(resultMessages.join('，'));

    // 重置状态
    setBatchAddresses('');
    setIsBatchImportVisible(false);
  };

  // 获取BTC地址信息的API调用
  const fetchBtcAddressInfo = async (address: string): Promise<{
    balance: number;
    utxoCount: number;
    largeUtxoCount: number;
    txCount: number;
  }> => {
    const UNISAT_API_KEY = 'bcb8e839a8c3791196970dab48ee02df83983d9aa03f4ff809a7264633ddd682';

    try {
      console.log(`获取 ${address} 的信息...`);

      // 使用UniSat API获取UTXO数据
      const utxoResponse = await fetch(`https://open-api.unisat.io/v1/indexer/address/${address}/utxo-data`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${UNISAT_API_KEY}`,
          'Accept': 'application/json',
        },
      });

      if (!utxoResponse.ok) {
        throw new Error(`UniSat API HTTP ${utxoResponse.status}`);
      }

      const utxoData = await utxoResponse.json();

      if (utxoData.code !== 0) {
        throw new Error(`UniSat API Error: ${utxoData.msg}`);
      }

      // 从UniSat API响应中提取数据
      const {
        total = 0,           // UTXO总数
        utxo = []           // UTXO数组
      } = utxoData.data || {};

      // 计算总余额（从所有UTXO的satoshi值求和）
      const totalSatoshi = utxo.reduce((sum: number, utxoItem: any) => sum + (utxoItem.satoshi || 0), 0);
      const balance = totalSatoshi / 100000000;

      // UTXO数量就是数组长度
      const utxoCount = utxoData.data.total;

      // 计算大于0.00001 BTC (1000 satoshi)的UTXO数量
      const largeUtxoCount = utxo.filter((utxoItem: any) => utxoItem.satoshi > 1000).length;

      // 获取交易数量 - 使用Blockstream API作为补充
      let txCount = 0;
      try {
        const addressResponse = await fetch(`https://blockstream.info/api/address/${address}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });

        if (addressResponse.ok) {
          const addressData = await addressResponse.json();
          txCount = addressData.chain_stats.tx_count || 0;
        }
      } catch (txError) {
        console.warn('获取交易数量失败，使用默认值:', txError);
      }

      const result = {
        balance,
        utxoCount,
        largeUtxoCount,
        txCount
      };

      console.log(`从UniSat API成功获取数据:`, result);
      return result;
    } catch (error) {
      console.error('UniSat API 调用失败:', error);

      // 备用方案 - 使用Blockstream API
      try {
        console.log(`尝试备用API获取 ${address} 的信息...`);

        const addressResponse = await fetch(`https://blockstream.info/api/address/${address}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });

        if (!addressResponse.ok) {
          throw new Error(`Blockstream API HTTP ${addressResponse.status}`);
        }

        const addressData = await addressResponse.json();

        // 计算余额（satoshi转BTC）
        const balance = (addressData.chain_stats.funded_txo_sum - addressData.chain_stats.spent_txo_sum) / 100000000;
        const txCount = addressData.chain_stats.tx_count;

        let utxoCount = 0;
        let largeUtxoCount = 0;

        // 如果有余额，尝试获取UTXO详情（可能会失败）
        if (balance > 0) {
          try {
            const utxoResponse = await fetch(`https://blockstream.info/api/address/${address}/utxo`);
            if (utxoResponse.ok) {
              const utxos = await utxoResponse.json();
              utxoCount = utxos.length;
              largeUtxoCount = utxos.filter((utxo: any) => utxo.value > 1000).length;
            }
          } catch (utxoError) {
            console.warn('Blockstream UTXO获取失败:', utxoError);
            // 使用估算值
            utxoCount = balance > 0 ? 1 : 0;
            largeUtxoCount = balance > 0.00001 ? 1 : 0;
          }
        }

        const result = {
          balance,
          utxoCount,
          largeUtxoCount,
          txCount
        };

        console.log(`从备用API成功获取数据:`, result);
        return result;
      } catch (backupError) {
        console.error('备用API也失败了:', backupError);
        throw new Error('所有API源都失败了');
      }
    }
  };

  // 刷新单个钱包数据
  const handleRefresh = useCallback(async (walletId: number) => {
    const wallet = wallets.find(w => w.id === walletId);
    if (!wallet) return;

    setIsLoading(prev => ({ ...prev, [walletId]: true }));

    try {
      const info = await fetchBtcAddressInfo(wallet.address);
      
      setWallets(prev => prev.map(w => 
        w.id === walletId 
          ? {
              ...w,
              balance: info.balance.toFixed(8),
              utxoCount: info.utxoCount,
              largeUtxoCount: info.largeUtxoCount,
              txCount: info.txCount
            }
          : w
      ));

      message.success(`${wallet.address.slice(0, 8)}... 数据刷新成功`);
    } catch (error) {
      console.error('刷新BTC钱包数据失败:', error);
      message.error(`刷新 ${wallet.address.slice(0, 8)}... 失败`);
    } finally {
      setIsLoading(prev => ({ ...prev, [walletId]: false }));
    }
  }, [wallets]);

  // 批量刷新所有数据
  const handleRefreshAll = async () => {
    if (wallets.length === 0) {
      message.warning('没有BTC地址需要刷新');
      return;
    }

    message.info('开始刷新所有BTC钱包数据...');

    // 分批处理，避免API限流，只处理普通钱包（排除合计行）
    const normalWallets = wallets.filter(w => typeof w.id === 'number');
    const batchSize = 3;
    for (let i = 0; i < normalWallets.length; i += batchSize) {
      const batch = normalWallets.slice(i, i + batchSize);
      const promises = batch.map(wallet => handleRefresh(wallet.id as number));

      await Promise.allSettled(promises);

      // 批次间延迟
      if (i + batchSize < normalWallets.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    message.success('所有BTC钱包数据刷新完成');
  };

  // 刷新选中的钱包
  const handleRefreshSelected = async () => {
    if (selectedWalletIds.size === 0) {
      message.warning('请先选择要刷新的钱包');
      return;
    }

    const selectedWallets = wallets.filter(w =>
      typeof w.id === 'number' && selectedWalletIds.has(w.id)
    );
    message.info(`开始刷新 ${selectedWallets.length} 个选中的BTC钱包...`);

    const promises = selectedWallets.map(wallet => handleRefresh(wallet.id as number));
    await Promise.allSettled(promises);

    message.success('选中的BTC钱包数据刷新完成');
  };

  // 组件初始化
  useEffect(() => {
    loadDataFromStorage();
  }, [loadDataFromStorage]);

  // 数据变化时保存
  useEffect(() => {
    if (wallets.length > 0) {
      const timeoutId = setTimeout(() => {
        saveDataToStorage();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [wallets, saveDataToStorage]);

  // 计算合计数据
  const calculateTotalData = useCallback(() => {
    return wallets.filter(w => typeof w.id === 'number').reduce((totals, wallet) => {
      const balance = parseFloat(wallet.balance || '0');
      return {
        totalBalance: totals.totalBalance + balance,
        totalUtxoCount: totals.totalUtxoCount + wallet.utxoCount,
        totalLargeUtxoCount: totals.totalLargeUtxoCount + wallet.largeUtxoCount,
        totalTxCount: totals.totalTxCount + wallet.txCount
      };
    }, {
      totalBalance: 0,
      totalUtxoCount: 0,
      totalLargeUtxoCount: 0,
      totalTxCount: 0
    });
  }, [wallets]);

  // 生成合计行数据
  const generateSummaryRow = useCallback(() => {
    if (!showRowTotal || wallets.length === 0) return null;

    const totals = calculateTotalData();

    return {
      id: 'summary',
      address: '合计',
      addressType: '',
      balance: totals.totalBalance.toFixed(8),
      utxoCount: totals.totalUtxoCount,
      largeUtxoCount: totals.totalLargeUtxoCount,
      txCount: totals.totalTxCount
    };
  }, [showRowTotal, wallets, calculateTotalData]);

  // 表格列定义
  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'index',
      width: 50,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => index + 1,
      fixed: 'left' as const
    },
    {
      title: 'BTC Address',
      dataIndex: 'address',
      key: 'address',
      width: 320,
      align: 'center' as const,
      fixed: 'left' as const,
      render: (address: string, record: any) => {
        // 合计行特殊处理
        if (record.id === 'summary') {
          return (
            <div style={{
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#1890ff',
              padding: '8px 0'
            }}>
              📊 合计 ({wallets.length} 个钱包)
            </div>
          );
        }

        // 普通钱包行
        return (
          <div style={{ width: '100%', textAlign: 'center', padding: '4px 0' }}>
            <Tooltip title={address}>
              <Typography.Text
                copyable={{ 
                  text: address, 
                  tooltips: false,
                  icon: <CopyOutlined style={{ fontSize: '12px' }} />
                }}
                style={{
                  fontSize: '11px',
                  fontFamily: 'monospace',
                  display: 'block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {isExpanded ? address : `${address.slice(0, 16)}...${address.slice(-12)}`}
              </Typography.Text>
            </Tooltip>
          </div>
        );
      }
    },
    {
      title: 'Balance (BTC)',
      dataIndex: 'balance',
      key: 'balance',
      width: 120,
      align: 'center' as const,
      render: (balance: string, record: any) => {
        // 合计行特殊处理
        if (record.id === 'summary') {
          const balanceNum = parseFloat(balance || '0');
          return (
            <div style={{
              textAlign: 'center',
              fontSize: '12px',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              color: '#1890ff',
              backgroundColor: '#f0f8ff',
              padding: '4px 8px',
              borderRadius: '4px'
            }}>
              {balanceNum.toFixed(8)}
            </div>
          );
        }

        // 普通行处理
        const loading = isLoading[record.id];
        if (loading) {
          return <Spin size="small" />;
        }

        const balanceNum = parseFloat(balance || '0');
        const color = balanceNum > 0 ? '#52c41a' : '#666';

        return (
          <div style={{
            textAlign: 'center',
            fontSize: '11px',
            fontFamily: 'monospace',
            color: color,
            fontWeight: balanceNum > 0 ? 'bold' : 'normal'
          }}>
            {balanceNum.toFixed(8)}
          </div>
        );
      }
    },
    {
      title: 'UTXO Count',
      dataIndex: 'utxoCount',
      key: 'utxoCount',
      width: 100,
      align: 'center' as const,
      render: (count: number, record: any) => {
        // 合计行特殊处理
        if (record.id === 'summary') {
          return (
            <div style={{
              textAlign: 'center',
              fontSize: '12px',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              color: '#1890ff',
              backgroundColor: '#f0f8ff',
              padding: '4px 8px',
              borderRadius: '4px'
            }}>
              {count}
            </div>
          );
        }

        // 普通行处理
        const loading = isLoading[record.id];
        if (loading) {
          return <Spin size="small" />;
        }

        return (
          <div style={{
            textAlign: 'center',
            fontSize: '11px',
            fontFamily: 'monospace'
          }}>
            {count || 0}
          </div>
        );
      }
    },
    {
      title: 'Large UTXO (>0.00001)',
      dataIndex: 'largeUtxoCount',
      key: 'largeUtxoCount',
      width: 140,
      align: 'center' as const,
      render: (count: number, record: any) => {
        // 合计行特殊处理
        if (record.id === 'summary') {
          return (
            <div style={{
              textAlign: 'center',
              fontSize: '12px',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              color: '#1890ff',
              backgroundColor: '#f0f8ff',
              padding: '4px 8px',
              borderRadius: '4px'
            }}>
              {count}
            </div>
          );
        }

        // 普通行处理
        const loading = isLoading[record.id];
        if (loading) {
          return <Spin size="small" />;
        }

        const color = count > 0 ? '#52c41a' : '#666';

        return (
          <div style={{
            textAlign: 'center',
            fontSize: '11px',
            fontFamily: 'monospace',
            color: color,
            fontWeight: count > 0 ? 'bold' : 'normal'
          }}>
            {count || 0}
          </div>
        );
      }
    },
    {
      title: 'TX Count',
      dataIndex: 'txCount',
      key: 'txCount',
      width: 100,
      align: 'center' as const,
      render: (count: number, record: any) => {
        // 合计行不显示TX数量
        if (record.id === 'summary') {
          return (
            <div style={{
              textAlign: 'center',
              fontSize: '12px',
              color: '#999',
              fontWeight: 'bold'
            }}>
              -
            </div>
          );
        }

        // 普通行处理
        const loading = isLoading[record.id];
        if (loading) {
          return <Spin size="small" />;
        }

        return (
          <div style={{
            textAlign: 'center',
            fontSize: '11px',
            fontFamily: 'monospace'
          }}>
            {count || 0}
          </div>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: any) => {
        // 合计行不显示操作按钮
        if (record.id === 'summary') {
          return null;
        }

        return (
          <Space size={4}>
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => handleRefresh(record.id)}
              loading={isLoading[record.id]}
              style={{
                color: '#1890ff',
                fontSize: '12px'
              }}
              title="刷新数据"
            />
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteWallet(record.id)}
              style={{
                color: '#ff4d4f',
                fontSize: '12px'
              }}
              title="删除钱包"
            />
          </Space>
        );
      }
    }
  ];

  // 导出功能选择
  const handleExportSelected = () => {
    if (selectedWalletIds.size === 0) {
      message.warning('请先选择要导出的钱包');
      return;
    }
    setIsExportModalVisible(true);
  };

  // 执行导出
  const executeExport = () => {
    if (selectedWalletIds.size === 0) {
      message.warning('请先选择要导出的钱包');
      return;
    }

    const selectedWallets = wallets.filter(w =>
      typeof w.id === 'number' && selectedWalletIds.has(w.id)
    );
    const today = new Date().toISOString().split('T')[0].replace(/-/g, '');

    if (exportFormat === 'json') {
      // JSON导出 - 包含完整配置信息
      const exportData = {
        exportTime: new Date().toISOString(),
        wallets: selectedWallets.map(wallet => ({
          address: wallet.address,
          addressType: wallet.addressType,
          balance: wallet.balance,
          utxoCount: wallet.utxoCount,
          largeUtxoCount: wallet.largeUtxoCount,
          txCount: wallet.txCount
        }))
      };

      const dataStr = safeStringify(exportData);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      saveAs(dataBlob, `btc-wallets-${today}.json`);

      message.success(`已导出 ${selectedWallets.length} 个BTC钱包的完整数据`);
    } else {
      // Excel导出
      const excelData: any[] = [];

      // 准备表头
      const headers = ['BTC地址', '地址类型', '余额(BTC)', 'UTXO数量', '大额UTXO数量(>0.00001)', '交易数量'];
      excelData.push(headers);

      // 准备数据行
      selectedWallets.forEach(wallet => {
        const row = [
          wallet.address,
          wallet.addressType,
          parseFloat(wallet.balance || '0').toFixed(8),
          wallet.utxoCount || 0,
          wallet.largeUtxoCount || 0,
          wallet.txCount || 0
        ];
        excelData.push(row);
      });

      // 创建工作表
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      // 设置列宽
      const colWidths = [
        { wch: 45 }, // BTC地址列
        { wch: 20 }, // 地址类型列
        { wch: 15 }, // 余额列
        { wch: 12 }, // UTXO数量列
        { wch: 18 }, // 大额UTXO数量列
        { wch: 12 }  // 交易数量列
      ];

      ws['!cols'] = colWidths;

      // 创建工作簿
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'BTC钱包信息');

      // 导出文件
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const excelBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(excelBlob, `btc-wallets-${today}.xlsx`);

      message.success(`已导出 ${selectedWallets.length} 个BTC钱包的Excel数据`);
    }

    setIsExportModalVisible(false);
  };

  // 删除选中的钱包
  const handleDeleteSelected = () => {
    if (selectedWalletIds.size === 0) {
      message.warning('请先选择要删除的钱包');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${selectedWalletIds.size} 个BTC钱包吗？此操作不可恢复。`,
      okText: '确认删除',
      cancelText: '取消',
      okType: 'danger',
      onOk: () => {
        setWallets(prev => prev.filter(w =>
          typeof w.id !== 'number' || !selectedWalletIds.has(w.id)
        ));
        setSelectedWalletIds(new Set());
        message.success(`已删除 ${selectedWalletIds.size} 个BTC钱包`);
      }
    });
  };

  // 处理文件导入
  const handleFileImport = async (file: File) => {
    try {
      const text = await file.text();
      const importData = JSON.parse(text);

      // 验证导入数据格式
      if (!importData.wallets || !Array.isArray(importData.wallets)) {
        message.error('无效的导入文件格式：缺少钱包数据');
        return false;
      }

      // 导入钱包数据
      const importedWallets = importData.wallets.map((wallet: any, index: number) => ({
        id: Date.now() + index,
        address: wallet.address,
        addressType: wallet.addressType || 'Unknown',
        balance: wallet.balance || '0',
        utxoCount: wallet.utxoCount || 0,
        largeUtxoCount: wallet.largeUtxoCount || 0,
        txCount: wallet.txCount || 0
      }));

      // 合并数据
      const mergedWallets = [...wallets];
      let addedCount = 0;
      let duplicateCount = 0;

      importedWallets.forEach((importedWallet: BtcWallet) => {
        const existing = wallets.find(w => w.address === importedWallet.address);
        if (!existing) {
          mergedWallets.push(importedWallet);
          addedCount++;
        } else {
          duplicateCount++;
        }
      });

      // 更新状态
      setWallets(mergedWallets);

      // 显示导入结果
      const resultMessages = [];
      if (addedCount > 0) resultMessages.push(`新增 ${addedCount} 个BTC钱包`);
      if (duplicateCount > 0) resultMessages.push(`跳过 ${duplicateCount} 个重复钱包`);

      message.success(`导入完成：${resultMessages.join('，')}`);
      setIsJsonImportVisible(false);
      setImportFileList([]);

      return false;
    } catch (error) {
      console.error('导入失败:', error);
      message.error('导入失败：文件格式错误或数据无效');
      return false;
    }
  };

  // 准备表格数据
  const tableData = useMemo(() => {
    const data = [...wallets];
    const summaryRow = generateSummaryRow();
    if (summaryRow) {
      data.push(summaryRow);
    }
    return data;
  }, [wallets, generateSummaryRow]);

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
              🪙 BTC钱包管理器
            </span>
            <Tag color="orange" style={{ fontSize: '12px' }}>
              支持所有BTC地址格式
            </Tag>
          </div>
        }
        style={{
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
        bodyStyle={{ padding: '16px' }}
      >
        {/* 控制面板 */}
        <div style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 16]} align="middle">
            {/* 操作按钮 */}
            <Col xs={24}>
              <Space wrap>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsAddWalletVisible(true)}
                  style={primaryButtonStyle}
                >
                  添加地址
                </Button>

                <Button
                  icon={<ImportOutlined />}
                  onClick={() => setIsBatchImportVisible(true)}
                  style={buttonStyle}
                >
                  批量导入
                </Button>

                <Button
                  icon={<FileExcelOutlined />}
                  onClick={() => setIsJsonImportVisible(true)}
                  style={buttonStyle}
                >
                  JSON导入
                </Button>

                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRefreshAll}
                  style={buttonStyle}
                  disabled={wallets.length === 0}
                >
                  刷新全部
                </Button>

                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRefreshSelected}
                  style={buttonStyle}
                  disabled={selectedWalletIds.size === 0}
                >
                  刷新选中
                </Button>

                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExportSelected}
                  style={buttonStyle}
                  disabled={selectedWalletIds.size === 0}
                >
                  导出选中
                </Button>

                <Button
                  icon={<DeleteOutlined />}
                  onClick={handleDeleteSelected}
                  style={buttonStyle}
                  disabled={selectedWalletIds.size === 0}
                  danger
                >
                  删除选中
                </Button>
              </Space>
            </Col>
          </Row>

          {/* 第三行：显示控制 */}
          <Row gutter={[16, 8]} align="middle" style={{ marginTop: '12px' }}>
            <Col>
              <Checkbox
                checked={showRowTotal}
                onChange={(e) => setShowRowTotal(e.target.checked)}
              >
                显示合计行
              </Checkbox>
            </Col>
            <Col>
              <Button
                type="text"
                icon={isExpanded ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={() => setIsExpanded(!isExpanded)}
                style={{ fontSize: '12px' }}
              >
                {isExpanded ? '隐藏地址' : '显示完整地址'}
              </Button>
            </Col>
            <Col>
              <span style={{ fontSize: '12px', color: '#666' }}>
                共 {wallets.length} 个BTC钱包
                {selectedWalletIds.size > 0 && ` (已选中 ${selectedWalletIds.size} 个)`}
              </span>
            </Col>
          </Row>
        </div>

        {/* 钱包表格 */}
        <Table
          columns={columns}
          dataSource={tableData}
          rowKey="id"
          pagination={false}
          scroll={{ x: 'max-content', y: 600 }}
          size="small"
          rowSelection={{
            selectedRowKeys: Array.from(selectedWalletIds),
            onChange: (selectedRowKeys) => {
              // 过滤掉合计行
              const validKeys = selectedRowKeys.filter(key => key !== 'summary') as number[];
              setSelectedWalletIds(new Set(validKeys));
            },
            getCheckboxProps: (record) => ({
              disabled: record.id === 'summary', // 合计行不可选
            }),
          }}
          style={{
            backgroundColor: 'white',
            borderRadius: '6px'
          }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🪙</div>
                <div style={{ fontSize: '16px', marginBottom: '8px' }}>暂无BTC钱包</div>
                <div style={{ fontSize: '12px' }}>
                  支持 Legacy (1...)、SegWit (3...)、Bech32 (bc1q...)、Taproot (bc1p...) 格式
                </div>
              </div>
            )
          }}
        />
      </Card>

      {/* 添加地址 Modal */}
      <Modal
        title="添加BTC地址"
        open={isAddWalletVisible}
        onOk={handleAddWallet}
        onCancel={() => {
          setIsAddWalletVisible(false);
          setNewWalletAddress('');
        }}
        okText="添加"
        cancelText="取消"
        width={500}
        okButtonProps={{ style: primaryButtonStyle }}
      >
        <div style={{ marginBottom: '12px' }}>
          <Typography.Text type="secondary">
            支持所有BTC地址格式：Legacy (1...)、SegWit (3...)、Bech32 (bc1q...)、Taproot (bc1p...)
          </Typography.Text>
        </div>
        <Input
          value={newWalletAddress}
          onChange={(e) => setNewWalletAddress(e.target.value)}
          placeholder="请输入BTC地址"
          style={componentStyle}
          onPressEnter={handleAddWallet}
        />
      </Modal>

      {/* 批量导入 Modal */}
      <Modal
        title="批量导入BTC地址"
        open={isBatchImportVisible}
        onOk={handleBatchImport}
        onCancel={() => {
          setIsBatchImportVisible(false);
          setBatchAddresses('');
        }}
        okText="导入"
        cancelText="取消"
        width={600}
        okButtonProps={{ style: primaryButtonStyle }}
      >
        <div style={{ marginBottom: '12px' }}>
          <Typography.Text type="secondary">
            每行一个BTC地址，支持所有格式：Legacy (1...)、SegWit (3...)、Bech32 (bc1q...)、Taproot (bc1p...)
          </Typography.Text>
        </div>
        <Input.TextArea
          value={batchAddresses}
          onChange={(e) => setBatchAddresses(e.target.value)}
          placeholder={`请输入BTC地址，每行一个，例如：
1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa
3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy
bc1qw508d6qejxtdg4y5r3zarvary0c5xw7kv8f3t4
bc1p5d7rjq7g6rdk2yhzks9smlaqtedr4dekq08ge8ztwac72sfr9rusxg3297`}
          rows={8}
          style={componentStyle}
        />
      </Modal>

      {/* JSON导入 Modal */}
      <Modal
        title="JSON文件导入"
        open={isJsonImportVisible}
        onCancel={() => {
          setIsJsonImportVisible(false);
          setImportFileList([]);
        }}
        footer={null}
        width={500}
      >
        <div style={{ marginBottom: '16px' }}>
          <Typography.Text type="secondary">
            选择之前导出的JSON文件进行导入
          </Typography.Text>
        </div>
        <Upload.Dragger
          fileList={importFileList}
          beforeUpload={handleFileImport}
          onRemove={() => setImportFileList([])}
          accept=".json"
          maxCount={1}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
          </p>
          <p className="ant-upload-text">点击或拖拽JSON文件到此区域</p>
          <p className="ant-upload-hint">
            支持单个JSON文件导入，文件将自动解析并合并到现有数据中
          </p>
        </Upload.Dragger>
      </Modal>

      {/* 导出选择 Modal */}
      <Modal
        title="导出选中的BTC钱包"
        open={isExportModalVisible}
        onOk={executeExport}
        onCancel={() => setIsExportModalVisible(false)}
        okText="导出"
        cancelText="取消"
        okButtonProps={{ style: primaryButtonStyle }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Typography.Text>
            已选中 {selectedWalletIds.size} 个BTC钱包，请选择导出格式：
          </Typography.Text>
        </div>
        <Select
          value={exportFormat}
          onChange={setExportFormat}
          style={{ width: '100%' }}
          options={[
            { value: 'json', label: 'JSON格式 (包含完整配置，可重新导入)' },
            { value: 'excel', label: 'Excel格式 (便于查看和分析)' }
          ]}
        />
      </Modal>
    </div>
  );
};

export default WalletBtc;
